import { useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { ApiError } from '../api/client'
import { useDeleteAccount, useMe } from '../api/hooks'
import { GuestsSection } from '../components/GuestsSection'
import { ScreenShell } from '../components/ScreenShell'
import { BackButton, SectionHeader, TopBar } from '../primitives'

export const AccountSettingsScreen = () => {
  const me = useMe()
  const navigate = useNavigate()

  if (me.isLoading) {
    return (
      <ScreenShell>
        <div style={{ padding: 16, color: 'var(--ink-3)' }}>Loading…</div>
      </ScreenShell>
    )
  }
  if (!me.data) return null

  return (
    <ScreenShell>
      <TopBar left={<BackButton fallback={() => navigate({ to: '/me' })} />} title="Account" />

      <SectionHeader kicker="sign-in" title="Auth methods" />
      <div style={{ padding: '0 16px' }}>
        <Card>
          <CardLine label="Method">Handle + sign-in code</CardLine>
          <CardLine label="Handle">@{me.data.handle}</CardLine>
          <p style={{ margin: 0, fontSize: 12, color: 'var(--ink-3)' }}>
            Lost your code or want to rotate it? Ping kwb on private chat for a fresh passphrase.
            Magic-link / Google / Apple sign-in are coming in v2.
          </p>
        </Card>
      </div>

      <SectionHeader kicker="email" title="Notifications" />
      <div style={{ padding: '0 16px' }}>
        <Card>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--ink-2)' }}>
            Email notifications (added to play, season ending, friend requests, etc.) are coming in
            v2. For now, in-app surfaces show the same signals — friend pending counts on Home, the
            active-seasons days-remaining pill, and per-play detail.
          </p>
        </Card>
      </div>

      <GuestsSection groupId={null} />

      <SectionHeader kicker="data" title="Export" />
      <div style={{ padding: '0 16px' }}>
        <Card>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--ink-2)' }}>
            CSV export of your plays, ratings, and history is coming in v2. For an early export, ask
            kwb for a one-off pull.
          </p>
        </Card>
      </div>

      <SectionHeader kicker="danger zone" title="Delete account" />
      <div style={{ padding: '0 16px' }}>
        <DeleteAccountCard handle={me.data.handle} />
      </div>
    </ScreenShell>
  )
}

const Card = ({ children }: { children: React.ReactNode }) => (
  <div
    className="felt-card"
    style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 8 }}
  >
    {children}
  </div>
)

const CardLine = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, fontSize: 13 }}>
    <span className="kicker" style={{ minWidth: 60, color: 'var(--ink-3)' }}>
      {label}
    </span>
    <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--ink)' }}>{children}</span>
  </div>
)

const DeleteAccountCard = ({ handle }: { handle: string }) => {
  const del = useDeleteAccount()
  const [confirming, setConfirming] = useState(false)
  const [confirmText, setConfirmText] = useState('')
  const [error, setError] = useState<string | null>(null)

  const matches = confirmText.trim().replace(/^@/, '') === handle

  const onDelete = async () => {
    if (!matches || del.isPending) return
    setError(null)
    try {
      await del.mutateAsync()
      window.location.href = '/sign-in'
    } catch (err) {
      setError(err instanceof ApiError ? err.friendlyMessage : 'Could not delete account.')
    }
  }

  return (
    <div
      className="felt-card"
      style={{
        padding: 14,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        borderColor: 'var(--terra-ink)',
      }}
    >
      <p style={{ margin: 0, fontSize: 13, color: 'var(--ink-2)' }}>
        Deleting your account replaces your handle and display name with a placeholder, clears your
        email and avatar, and signs you out everywhere. Your past plays remain visible to other
        group members so the rating history stays consistent; you'll appear as{' '}
        <span style={{ fontFamily: 'var(--font-mono)' }}>[deleted user]</span>. This can't be
        undone.
      </p>

      {!confirming ? (
        <button
          type="button"
          className="btn-ghost"
          onClick={() => setConfirming(true)}
          style={{ alignSelf: 'flex-start', color: 'var(--terra-ink)' }}
        >
          Delete account
        </button>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span className="kicker">type your handle to confirm</span>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.currentTarget.value)}
              placeholder={handle}
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              autoFocus
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 14,
                padding: '6px 10px',
                border: '1px solid var(--line-soft)',
                borderRadius: 3,
              }}
            />
          </label>
          {error && <div style={{ fontSize: 12, color: 'var(--terra-ink)' }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              className="btn-ghost"
              onClick={() => {
                setConfirming(false)
                setConfirmText('')
                setError(null)
              }}
              disabled={del.isPending}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-stamp"
              onClick={onDelete}
              disabled={!matches || del.isPending}
              style={{
                background: matches ? 'var(--terra-ink)' : undefined,
                opacity: matches ? 1 : 0.5,
              }}
            >
              {del.isPending ? 'Deleting…' : 'Permanently delete'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
